import { useCallback } from "react";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";

type DeleteCompositionsBatchResult = {
    replacementCompositionId?: string | null;
};

type DeleteCompositionsBatchFn = (args: {
    projectId: string;
    compositionIds: string[];
    createReplacementIfEmpty?: boolean;
}) => Promise<DeleteCompositionsBatchResult>;

type StartCompositionSwitchFn = (args: {
    compositionId: string;
    compositionTitle?: string | null;
}) => void;

type DeleteCompositionsArgs = {
    compositionIds: string[];
    preferredFallbackCompositionId?: string | null;
    onBeforeDelete?: () => Promise<void> | void;
    onAfterDelete?: (result: DeleteCompositionsBatchResult) => Promise<void> | void;
};

export const useDeleteCompositionsPreservingProject = ({
    projectId,
    selectedCompositionId,
    deleteCompositionsBatch,
    startCompositionSwitch,
    setIsRestoringComposition,
}: {
    projectId: string | null | undefined;
    selectedCompositionId: string | null;
    deleteCompositionsBatch: DeleteCompositionsBatchFn;
    startCompositionSwitch: StartCompositionSwitchFn;
    setIsRestoringComposition: (value: boolean) => void;
}) => {
    return useCallback(async ({
        compositionIds,
        preferredFallbackCompositionId,
        onBeforeDelete,
        onAfterDelete,
    }: DeleteCompositionsArgs) => {
        const normalizedProjectId = String(projectId || "").trim();
        const normalizedCompositionIds = Array.from(
            new Set(
                (compositionIds || [])
                    .map((compositionId) => String(compositionId || "").trim())
                    .filter(Boolean),
            ),
        );
        if (!normalizedProjectId || normalizedCompositionIds.length === 0) {
            return { replacementCompositionId: null };
        }

        const compositions = ProjectAssetsStore.projectCompositions || [];
        const remainingCompositions = compositions.filter((composition: any) => {
            const compositionId = String(composition?.id || "").trim();
            return compositionId && !normalizedCompositionIds.includes(compositionId);
        });
        const shouldCreateReplacement = remainingCompositions.length === 0;

        const selectedId = String(selectedCompositionId || "").trim();
        const isDeletingSelectedComposition = Boolean(
            selectedId && normalizedCompositionIds.includes(selectedId),
        );

        if (isDeletingSelectedComposition && !shouldCreateReplacement) {
            const preferredFallbackId = String(preferredFallbackCompositionId || "").trim();
            const fallbackComposition =
                remainingCompositions.find(
                    (composition: any) => String(composition?.id || "").trim() === preferredFallbackId,
                ) ||
                remainingCompositions[0];

            const fallbackId = String(fallbackComposition?.id || "").trim();
            if (fallbackId) {
                startCompositionSwitch({
                    compositionId: fallbackId,
                    compositionTitle: fallbackComposition?.title ?? fallbackComposition?.name ?? null,
                });
            }
        }

        if (shouldCreateReplacement) {
            setIsRestoringComposition(true);
        }

        await onBeforeDelete?.();

        const result = await deleteCompositionsBatch({
            projectId: normalizedProjectId,
            compositionIds: normalizedCompositionIds,
            createReplacementIfEmpty: shouldCreateReplacement,
        });

        if (shouldCreateReplacement) {
            const replacementCompositionId = String(result?.replacementCompositionId || "").trim();
            if (replacementCompositionId) {
                startCompositionSwitch({
                    compositionId: replacementCompositionId,
                    compositionTitle: "Untitled",
                });
            } else {
                setIsRestoringComposition(false);
            }
        }

        await onAfterDelete?.(result);

        return result;
    }, [
        deleteCompositionsBatch,
        projectId,
        selectedCompositionId,
        setIsRestoringComposition,
        startCompositionSwitch,
    ]);
};
